/**
 * Staffelblok — Bouwbrief 01 §5 D4g, Antwoord 01 §4C.
 *
 * Dit blok bezit de hoeveelheid. De gekozen rij schrijft zijn aantal in een
 * verborgen veld dat via het form-attribuut aan Horizon's productformulier
 * hangt; product-form.js bouwt zijn payload met new FormData(form) en neemt
 * dat veld dus mee. Hoofdknop en plakbalk versturen daardoor per definitie
 * dezelfde hoeveelheid — er is maar één formulier en één veld.
 *
 * Het element zendt daarnaast twee events uit:
 *
 *   - `quantity-selector:update` — Horizon's eigen event. Zijn ingebouwde
 *     plakbalk (sticky-add-to-cart.js) luistert hierop om het aantal in de
 *     knoptekst te tonen. Zonder dit toont die balk "1" terwijl er 2 in de
 *     winkelwagen gaat. `cartLine` blijft leeg: dat veld onderscheidt de
 *     productpagina van de winkelwagenlade.
 *   - `mivoza:tier-change` — voor onze eigen onderdelen.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const EVENT = 'mivoza:tier-change';
const HORIZON_QUANTITY_EVENT = 'quantity-selector:update';

class MivozaTiers extends HTMLElement {
  /** Oorspronkelijke, server-gerenderde bedragen per trede. */
  #origineel = new Map();

  connectedCallback() {
    this.addEventListener('change', this.#onChange);

    for (const rij of this.querySelectorAll('.mv-tier')) {
      this.#origineel.set(rij, {
        amt: rij.querySelector('.mv-tier__amt')?.innerHTML ?? '',
        per: rij.querySelector('[data-per-amount]')?.textContent ?? '',
        save: rij.querySelector('[data-save-amount]')?.textContent ?? '',
      });
    }

    // Het toevoegen wordt overgenomen zodra er varianten gemengd worden.
    //
    // Dat gebeurt bij de KLIK en niet bij het versturen, en dat is geen
    // willekeurige keuze. Horizon bindt zijn on:-events op document in de
    // capture-fase (assets/component.js regel 239). Een submit-listener op
    // het formulier — zelfs in capture — komt daar dus altijd ná, en
    // stopImmediatePropagation is dan te laat: het artikel werd twee keer
    // toegevoegd, één keer door ons en één keer door Horizon. Nagemeten:
    // 2 stuks werden er 4.
    //
    // Bij de klik is die volgordevraag weg. De bestelknop heeft geen
    // on:click, dus Horizon doet daar niets; wij roepen preventDefault aan en
    // dan ontstaat er simpelweg nooit een submit-event om af te handelen.
    document.addEventListener('click', this.#onKlik, { capture: true });

    // De begintoestand staat al in de HTML (checked + .is-on). Het verborgen
    // veld ook. Hier alleen synchroniseren voor het geval de browser een
    // eerdere keuze heeft hersteld na een terugnavigatie.
    const checked = this.#checked;
    if (checked) this.#apply(checked);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    document.removeEventListener('click', this.#onKlik, { capture: true });
  }

  get #form() {
    const id = this.dataset.formId;
    return id ? document.getElementById(id) : null;
  }

  get #pick() {
    return this.querySelector('[data-mv-pick]');
  }

  /** Wordt er daadwerkelijk gemengd? Zo nee, dan doet Horizon zijn eigen werk. */
  get #mengsel() {
    const pick = this.#pick;
    return Boolean(pick) && !pick.hidden;
  }

  /** De keuzemenu's die nu meetellen — verborgen rijen tellen niet mee. */
  get #keuzes() {
    if (!this.#pick) return [];
    return [...this.#pick.querySelectorAll('.mv-pick__row')]
      .filter((rij) => !rij.hidden)
      .map((rij) => rij.querySelector('[data-mv-pick-select]'))
      .filter(Boolean);
  }

  /**
   * Zet centen om naar de opmaak van de winkel.
   *
   * Shopify.formatMoney bestaat in dit thema niet, dus het patroon komt uit
   * shop.money_format (meegegeven door het blok). De vijf varianten die
   * Shopify kent worden allemaal ondersteund, zodat dit ook klopt zodra er
   * een CHF-opmaak met apostrof bijkomt.
   */
  #geld(centen) {
    const patroon = this.#pick?.dataset.moneyFormat || '\u20ac{{amount_with_comma_separator}}';
    const bedrag = centen / 100;

    const knip = (decimalen, decimaalteken, duizendteken) => {
      const vast = bedrag.toFixed(decimalen);
      const [heel, deel] = vast.split('.');
      const metDuizend = heel.replace(/\B(?=(\d{3})+(?!\d))/g, duizendteken);
      return decimalen > 0 ? metDuizend + decimaalteken + deel : metDuizend;
    };

    return patroon
      .replace('{{amount_no_decimals_with_comma_separator}}', knip(0, '', '.'))
      .replace('{{amount_with_comma_separator}}', knip(2, ',', '.'))
      .replace('{{amount_with_apostrophe_separator}}', knip(2, '.', "'"))
      .replace('{{amount_no_decimals}}', knip(0, '', ','))
      .replace('{{amount}}', knip(2, '.', ','));
  }

  /** Zet alle treden terug op wat de server heeft gerenderd. */
  #herstelBedragen() {
    for (const [rij, orig] of this.#origineel) {
      const amt = rij.querySelector('.mv-tier__amt');
      if (amt) amt.innerHTML = orig.amt;
      const per = rij.querySelector('[data-per-amount]');
      if (per) per.textContent = orig.per;
      const save = rij.querySelector('[data-save-amount]');
      if (save) save.textContent = orig.save;
    }
  }

  /**
   * Toont zoveel keuzemenu's als de gekozen trede groot is.
   * Bij één stuk of één variant valt het blok weg en doet Horizon het werk.
   */
  #tekenKeuzes(aantal) {
    const pick = this.#pick;
    if (!pick) return;

    pick.hidden = aantal < 2;
    for (const rij of pick.querySelectorAll('.mv-pick__row')) {
      rij.hidden = Number(rij.dataset.mvPickRow) > aantal;
    }
  }

  /**
   * Telt het bedrag van de gekozen trede op uit de gekozen varianten.
   *
   * Nodig omdat 194 van de 757 producten met varianten onderling in prijs
   * verschillen: een vast staffelbedrag zou daar iets anders beweren dan de
   * checkout rekent. Bij gelijke prijzen komt er hetzelfde bedrag uit als de
   * server al had gerenderd.
   */
  #herrekenTotaal() {
    this.#herstelBedragen();
    if (!this.#mengsel) return;

    const rij = this.querySelector('.mv-tier.is-on');
    const keuzes = this.#keuzes;
    if (!rij || !keuzes.length) return;

    const korting = Number(rij.dataset.disc) || 0;

    // Groeperen per variant, want zo gaan ze ook de winkelwagen in: twee keer
    // dezelfde kleur is één regel van 2, niet twee regels van 1.
    //
    // En dat is niet alleen netjes, het bepaalt de cent. Shopify rondt de
    // korting PER STUK af en vermenigvuldigt daarna — niet andersom.
    // Nagemeten op de Fleecedecke: 2x €49,95 met 10% wordt 4995 x 0,9 =
    // 4495,5 -> 4496 per stuk, maal twee is €89,92. Rekenen over het
    // regeltotaal geeft €89,91, en dan belooft de pagina een cent minder dan
    // de checkout rekent.
    const regels = new Map();
    for (const sel of keuzes) {
      const optie = sel.options[sel.selectedIndex];
      const id = sel.value;
      const prijs = Number(optie?.dataset.price) || 0;
      const bestaand = regels.get(id);
      if (bestaand) bestaand.aantal += 1;
      else regels.set(id, { prijs, aantal: 1 });
    }

    let vergelijk = 0;
    let totaal = 0;
    for (const { prijs, aantal } of regels.values()) {
      vergelijk += prijs * aantal;
      totaal += Math.round((prijs * (100 - korting)) / 100) * aantal;
    }

    const bespaard = vergelijk - totaal;
    const perStuk = Math.round(totaal / keuzes.length);

    // innerHTML en niet textContent: shop.money_format bevat in deze winkel
    // markup (<span class=cvc-money>), gezet door de valuta-omrekenapp.
    // Als tekst invoegen laat de tags letterlijk zien en breekt bovendien de
    // omrekening naar CHF. De bron is een winkelinstelling, geen invoer van
    // bezoekers, dus dit is geen open deur.
    const amt = rij.querySelector('.mv-tier__amt');
    if (amt) {
      amt.innerHTML = korting > 0 ? `${this.#geld(totaal)}<s>${this.#geld(vergelijk)}</s>` : this.#geld(totaal);
    }

    const per = rij.querySelector('[data-per-amount]');
    if (per) per.innerHTML = this.#geld(perStuk);

    const save = rij.querySelector('[data-save-amount]');
    if (save) save.innerHTML = this.#geld(bespaard);

    this.#vulDetail();
  }

  /**
   * Spiegelt de bedragen van de gekozen kaart naar de regel eronder.
   *
   * In de kaarten zelf is geen ruimte voor totaal, vanaf-prijs en besparing —
   * die staan er wel in, maar verborgen. Dit is de enige plek waar ze in beeld
   * komen, en ze komen uit de kaart zodat er één bron blijft: wat Liquid heeft
   * gerenderd, of wat #herrekenTotaal er na een variantwissel in heeft gezet.
   */
  #vulDetail() {
    const rij = this.querySelector('.mv-tier.is-on');
    const detail = this.querySelector('[data-tier-detail]');
    if (!rij || !detail) return;

    const aantal = detail.querySelector('[data-detail-count]');
    const bronAantal = rij.querySelector('[data-card-count]');
    if (aantal && bronAantal) aantal.textContent = bronAantal.textContent.trim();

    const heeftKorting = (Number(rij.dataset.disc) || 0) > 0;

    // Geen korting, niets te melden: de prijs staat al in het prijsblok erboven.
    detail.hidden = !heeftKorting;

    const besparing = detail.querySelector('[data-detail-save]');
    const bronBesparing = rij.querySelector('.mv-tier__save');
    if (besparing) {
      besparing.hidden = !heeftKorting;
      besparing.innerHTML = heeftKorting && bronBesparing ? bronBesparing.innerHTML.trim() : '';
    }

    const totaal = detail.querySelector('[data-detail-total]');
    const bronBedrag = rij.querySelector('.mv-tier__amt');
    if (totaal && bronBedrag) {
      // Klonen en de doorgestreepte prijs eruit halen, zodat wat overblijft
      // het te betalen bedrag is — ook als dat in een <span> van de
      // valuta-app zit.
      const kloon = bronBedrag.cloneNode(true);
      const streep = kloon.querySelector('s');
      if (streep) streep.remove();
      totaal.innerHTML = `<b>${kloon.innerHTML.trim()}</b>${streep ? `<s>${streep.innerHTML}</s>` : ''}`;
    }
  }

  #onKlik = (event) => {
    if (!this.#mengsel) return;

    const doel = event.target;
    if (!(doel instanceof Element)) return;

    const knop = doel.closest('button[type="submit"], input[type="submit"]');
    // .form lost zowel een knop binnen het formulier op als een knop die er
    // via het form-attribuut aan hangt — dat laatste is de plakbalk onderaan.
    if (!knop || knop.form !== this.#form) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#voegToe();
  };

  /**
   * Voegt de gekozen varianten toe als aparte regels.
   *
   * Horizon's formulier kan maar één variant per keer; vier verschillende
   * kleuren zijn vier regels. Het verversen loopt via CartLinesUpdateEvent,
   * hetzelfde pad dat product-form.js gebruikt, zodat lade, mandje en header
   * vanzelf meelopen.
   */
  async #voegToe() {
    const knop = this.#form?.querySelector('[type="submit"]');
    if (knop) knop.setAttribute('aria-busy', 'true');

    const items = [];
    for (const sel of this.#keuzes) {
      const id = Number(sel.value);
      if (!id) continue;
      const bestaand = items.find((i) => i.id === id);
      if (bestaand) bestaand.quantity += 1;
      else items.push({ id, quantity: 1 });
    }
    if (!items.length) {
      if (knop) knop.removeAttribute('aria-busy');
      return;
    }

    const wortel = window.Shopify?.routes?.root ?? '/';
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');

    const beloofd = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: beloofd.promise,
      })
    );

    try {
      const res = await fetch(`${wortel}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections }),
      });
      if (!res.ok) {
        const fout = await res.json().catch(() => null);
        throw new Error(fout?.description || fout?.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const cart = await fetch(`${wortel}cart.js`).then((r) => r.json());

      beloofd.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'mivoza-tiers',
          sourceId: this.dataset.formId ?? 'tiers',
          itemCount: items.reduce((n, i) => n + i.quantity, 0),
          sections: data?.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error('[mivoza-tiers]', error);
      beloofd.reject(error);
    } finally {
      if (knop) knop.removeAttribute('aria-busy');
    }
  }

  get #checked() {
    return this.querySelector('.mv-tier__input:checked');
  }

  get #quantityField() {
    return this.querySelector('input[name="quantity"]');
  }

  /** @returns {number} het aantal dat nu aan het formulier hangt */
  get quantity() {
    return Number(this.#quantityField?.value) || 1;
  }

  /**
   * Werkt de mijlpaalbalk onder de treden bij.
   *
   * De begintoestand komt uit Liquid (blocks/mivoza-tiers.liquid), zodat er
   * ook zonder JavaScript een kloppende balk staat. Deze functie rekent
   * hetzelfde uit bij elke andere keuze — de formule moet dus gelijk blijven
   * aan die in het blok en aan snippets/mivoza-cart-steps.liquid.
   *
   * De vulling loopt van trede tot trede en niet lineair over het aantal:
   * alleen zo staat de bol precies op de mijlpaal zodra die gehaald is.
   */
  #tekenBalk(aantal) {
    const balk = this.querySelector('.mv-steps--pdp');
    if (!balk) return;

    const mijlpalen = (balk.dataset.ladder || '')
      .split(',')
      .map((deel) => deel.split(':').map(Number))
      .filter(([qty]) => qty > 0);
    if (!mijlpalen.length) return;

    let bereikt = 0;
    let volgende = null;
    for (const [qty, disc] of mijlpalen) {
      if (aantal >= qty) bereikt += 1;
      else if (!volgende) volgende = { qty, disc };
    }

    const stap = 100 / mijlpalen.length;
    let vulling = 100;
    if (volgende) {
      const vorige = bereikt > 0 ? mijlpalen[bereikt - 1][0] : 0;
      const gat = volgende.qty - vorige;
      const erin = gat > 0 ? Math.round(((aantal - vorige) / gat) * stap) : 0;
      vulling = Math.round(bereikt * stap) + erin;
    }

    const fill = balk.querySelector('[data-steps-fill]');
    if (fill) fill.style.width = `${Math.max(0, Math.min(100, vulling))}%`;

    for (const pin of balk.querySelectorAll('[data-steps-pin]')) {
      pin.classList.toggle('is-done', aantal >= Number(pin.dataset.stepsPin));
    }

    const regel = balk.querySelector('[data-steps-line]');
    if (!regel) return;

    const { before = '', middle = '', unit = '', maxLabel = '' } = balk.dataset;
    if (!volgende) {
      regel.innerHTML = '';
      regel.append(Object.assign(document.createElement('b'), { textContent: maxLabel }));
      return;
    }

    const resterend = volgende.qty - aantal;
    // Met textContent en append in plaats van innerHTML: de teksten komen uit
    // thema-instellingen en horen als tekst behandeld te worden, niet als markup.
    regel.textContent = `${before} `;
    regel.append(Object.assign(document.createElement('b'), { textContent: `${resterend} ${unit}` }));
    regel.append(` ${middle} `);
    regel.append(Object.assign(document.createElement('b'), { textContent: `${volgende.disc} %` }));
  }

  #onChange = (event) => {
    const doel = event.target;

    if (doel instanceof HTMLSelectElement && doel.hasAttribute('data-mv-pick-select')) {
      this.#herrekenTotaal();
      return;
    }

    if (!(doel instanceof HTMLInputElement) || !doel.classList.contains('mv-tier__input')) return;
    this.#apply(doel);
  };

  #apply(input) {
    for (const label of this.querySelectorAll('.mv-tier')) {
      label.classList.toggle('is-on', label.contains(input));
    }

    const quantity = Number(input.value) || 1;
    this.#tekenBalk(quantity);
    this.#tekenKeuzes(quantity);
    this.#herrekenTotaal();
    this.#vulDetail();
    const field = this.#quantityField;
    if (field) field.value = String(quantity);

    // Horizon's plakbalk leest hieruit hoeveel stuks de knop gaat toevoegen.
    const horizonEvent = new Event(HORIZON_QUANTITY_EVENT, { bubbles: true });
    horizonEvent.detail = { quantity, cartLine: undefined };
    this.dispatchEvent(horizonEvent);

    const row = input.closest('.mv-tier');
    this.dispatchEvent(
      new CustomEvent(EVENT, {
        bubbles: true,
        detail: {
          quantity,
          // De opgemaakte bedragen staan al in de DOM; doorgeven scheelt de
          // plakbalk een eigen berekening en daarmee een tweede waarheid.
          total: row?.querySelector('.mv-tier__amt')?.firstChild?.textContent?.trim() ?? null,
          compareAt: row?.querySelector('.mv-tier__amt s')?.textContent?.trim() ?? null,
        },
      })
    );
  }
}

if (!customElements.get('mivoza-tiers')) {
  customElements.define('mivoza-tiers', MivozaTiers);
}
